import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import {
  App,
  Button,
  Card,
  Dropdown,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tag,
  theme,
  Typography,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  DownOutlined,
  MailOutlined,
  UserAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useInvitationCreate,
  useInvitationRevoke,
  useOrganizationFindMembers,
  useOrganizationRemoveMember,
  useOrganizationUpdateMember,
} from '../../../libs/ohat-api';
import { getApiErrorMessage, isLimitExceeded } from '../../../utils/apiError';
import useMobile from '../../../hooks/useMobile';
import { MemberPageBreadCrumb } from '../../components/breadcrumb/MemberPageBreadCrumb';
import { WithdrawalPanel } from '../../components/WithdrawalPanel';

export const MemberListPage = () => {
  const { t } = useTranslation();
  const { account, isOwner } = useAuth();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { isMobile } = useMobile();
  const mobile = isMobile();
  const orgId = account?.organizationId ?? '';
  const owner = isOwner();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form] = Form.useForm<{ email: string; name: string }>();

  const {
    data,
    isLoading,
    refetch,
  } = useOrganizationFindMembers(orgId, {
    query: { enabled: !!orgId },
  });
  // findMembers はレスポンスDTO未定義のため型が付かない。実体は { accounts, invitations }。
  const membersData = data as
    | { accounts: any[]; invitations: any[] }
    | undefined;

  const invite = useInvitationCreate();
  const revoke = useInvitationRevoke();
  const updateMember = useOrganizationUpdateMember();
  const removeMember = useOrganizationRemoveMember();

  const accounts = membersData?.accounts ?? [];
  const invitations = membersData?.invitations ?? [];

  // 成功トースト＋再取得を共通化。失敗時はAPIメッセージを表示する。
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      message.success(ok);
      refetch();
    } catch (e: any) {
      message.error(getApiErrorMessage(e, t('common.processFailed')));
    }
  };

  const handleInvite = async () => {
    const values = await form.validateFields();
    try {
      await invite.mutateAsync({
        data: { email: values.email, name: values.name },
      });
      message.success(t('member.inviteSent'));
      setInviteOpen(false);
      form.resetFields();
      refetch();
    } catch (e: any) {
      if (isLimitExceeded(e)) {
        modal.confirm({
          title: t('member.limitTitle'),
          content: t('member.limitDesc'),
          okText: t('member.viewPlan'),
          cancelText: t('member.close'),
          onOk: () => navigate('/plan'),
        });
      } else {
        message.error(getApiErrorMessage(e, t('member.inviteFailed')));
      }
    }
  };

  const handleToggleStatus = (accountId: string, status: string) => {
    const newStatus = status === 'active' ? 'inactive' : 'active';
    return run(
      () => updateMember.mutateAsync({ id: orgId, accountId, data: { status: newStatus as any } }),
      newStatus === 'active' ? t('member.activated') : t('member.deactivated'),
    );
  };

  const handleToggleRole = (accountId: string, role: string) => {
    const newRole = role === 'owner' ? 'member' : 'owner';
    return run(
      () => updateMember.mutateAsync({ id: orgId, accountId, data: { role: newRole as any } }),
      t('member.roleChanged'),
    );
  };

  // 「組織から除外」は一旦メニューから非表示。復活に備えてロジックは残す。
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRemove = (accountId: string) =>
    run(() => removeMember.mutateAsync({ id: orgId, accountId }), t('member.removed'));

  const handleRevoke = (id: string) =>
    run(() => revoke.mutateAsync({ id }), t('member.revoked'));

  const handleInviteResend = (email: string) =>
    run(() => invite.mutateAsync({ data: { email } }), t('member.resendSuccess'));

  const renderRoleTag = (role: string) => (
    <Tag color={role === 'owner' ? 'blue' : 'default'}>
      {role === 'owner' ? t('member.roleOwner') : t('member.roleGeneral')}
    </Tag>
  );

  const renderStatusTag = (status: string) => (
    <Tag color={status === 'active' ? 'green' : 'red'}>
      {status === 'active' ? t('member.statusActive') : t('member.statusInactive')}
    </Tag>
  );

  // 操作系はテーブル列とモバイルカードで同じ実装を共有する（確認ダイアログの二重定義を避ける）。
  const renderMemberAction = (record: any, block = false) => {
    if (!owner || record.id === account?.id) return null;
    const items: MenuProps['items'] = [
      {
        key: 'role',
        label: record.role === 'owner' ? t('member.changeRoleToGeneral') : t('member.changeRoleToOwner'),
        onClick: () =>
          modal.confirm({
            title: t('member.confirmChangeRole', { role: record.role === 'owner' ? t('member.roleGeneral') : t('member.roleOwner') }),
            okText: t('common.submit') || 'Yes',
            cancelText: t('common.cancel') || 'Cancel',
            onOk: () => handleToggleRole(record.id, record.role),
          }),
      },
      {
        key: 'status',
        label: record.status === 'active' ? t('member.deactivate') : t('member.activate'),
        danger: record.status === 'active',
        onClick: () =>
          modal.confirm({
            title: record.status === 'active' ? t('member.confirmDeactivate') : t('member.confirmActivate'),
            okText: t('common.submit') || 'Yes',
            okButtonProps:
              record.status === 'active' ? { danger: true } : undefined,
            cancelText: t('common.cancel') || 'Cancel',
            onOk: () => handleToggleStatus(record.id, record.status),
          }),
      },
      // 「組織から除外」は一旦非表示（handleRemove ロジックは残置）
    ];
    return (
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button size={block ? 'middle' : 'small'} block={block}>
          {t('member.action')} <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  const renderInvitationAction = (record: any, block = false) => {
    if (!owner) return null;
    const resendButton = (
      <Button
        size={block ? 'middle' : 'small'}
        block={block}
        onClick={() => handleInviteResend(record.email)}
      >
        {t('member.resend')}
      </Button>
    );
    const revokeButton = (
      <Popconfirm title={t('member.confirmRevoke')} onConfirm={() => handleRevoke(record.id)}>
        <Button size={block ? 'middle' : 'small'} block={block} danger>
          {t('member.revoke')}
        </Button>
      </Popconfirm>
    );
    // モバイルは2つのボタンを等幅にして1行に収める。
    if (block) {
      return (
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>{resendButton}</div>
          <div style={{ flex: 1 }}>{revokeButton}</div>
        </div>
      );
    }
    return (
      <Space>
        {resendButton}
        {revokeButton}
      </Space>
    );
  };

  const memberColumns = [
    { title: t('member.name'), dataIndex: 'name', key: 'name', render: (v: string) => v || '-' },
    { title: t('member.email'), dataIndex: 'email', key: 'email' },
    {
      title: t('member.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => renderRoleTag(role),
    },
    {
      title: t('member.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status),
    },
    {
      title: t('member.action'),
      key: 'action',
      render: (_: any, record: any) => renderMemberAction(record),
    },
  ];

  const invitationColumns = [
    {
      title: t('member.name'),
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => v || '-',
    },
    { title: t('member.email'), dataIndex: 'email', key: 'email' },
    {
      title: t('member.expiresAt'),
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (v: string) => (v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '-'),
    },
    {
      title: t('member.action'),
      key: 'action',
      render: (_: any, record: any) => renderInvitationAction(record),
    },
  ];

  // モバイル一覧の行スタイル。Table が持っていた区切り線を li で再現する。
  const listItemStyle = (isLast: boolean) => ({
    padding: `${token.paddingSM}px 0`,
    borderBlockEnd: isLast ? undefined : `1px solid ${token.colorSplit}`,
  });

  const renderEmpty = () => (
    <div style={{ padding: token.padding, textAlign: 'center' as const }}>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </div>
  );

  // モバイルはテーブルだと横に溢れるため、1件1ブロックの縦積みで表示する。
  const renderMemberCards = () => {
    if (isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
    if (accounts.length === 0) return renderEmpty();
    return (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {accounts.map((record: any, index: number) => {
          const action = renderMemberAction(record, true);
          return (
            <li key={record.id} style={listItemStyle(index === accounts.length - 1)}>
              <Typography.Text strong style={{ display: 'block' }}>
                {record.name || '-'}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, wordBreak: 'break-all' }}
              >
                {record.email}
              </Typography.Text>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: token.marginXXS,
                  marginTop: token.marginXS,
                }}
              >
                {renderRoleTag(record.role)}
                {renderStatusTag(record.status)}
              </div>
              {action && <div style={{ marginTop: token.marginSM }}>{action}</div>}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderInvitationCards = () => (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {invitations.map((record: any, index: number) => {
        const action = renderInvitationAction(record, true);
        return (
          <li key={record.id} style={listItemStyle(index === invitations.length - 1)}>
            <Typography.Text strong style={{ display: 'block' }}>
              {record.name || '-'}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, wordBreak: 'break-all' }}
            >
              {record.email}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', fontSize: 12, marginTop: token.marginXXS }}
            >
              {t('member.expiresAt')}:{' '}
              {record.expiresAt ? dayjs(record.expiresAt).format('YYYY/MM/DD HH:mm') : '-'}
            </Typography.Text>
            {action && <div style={{ marginTop: token.marginSM }}>{action}</div>}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div style={{ width: '100%', maxWidth: 960 }}>
      <div style={{ marginBottom: 16 }}>
        <MemberPageBreadCrumb />
      </div>
      <div
        style={{
          display: 'flex',
          // モバイルはタイトルと招待ボタンを縦積みにしてボタンが潰れないようにする。
          flexDirection: mobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: mobile ? 'stretch' : 'center',
          gap: mobile ? token.marginSM : undefined,
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>{t('member.title')}</h2>
        {owner && (
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            block={mobile}
            onClick={() => setInviteOpen(true)}
          >
            {t('member.invite')}
          </Button>
        )}
      </div>

      <Card
        title={t('member.members')}
        style={{ marginBottom: 16 }}
        styles={{ body: { padding: mobile ? 12 : undefined } }}
      >
        {mobile ? (
          renderMemberCards()
        ) : (
          <Table
            dataSource={accounts}
            columns={memberColumns}
            rowKey="id"
            loading={isLoading}
            pagination={false}
          />
        )}
      </Card>

      {invitations.length > 0 && (
        <Card
          title={t('member.inviting')}
          styles={{ body: { padding: mobile ? 12 : undefined } }}
        >
          {mobile ? (
            renderInvitationCards()
          ) : (
            <Table
              dataSource={invitations}
              columns={invitationColumns}
              rowKey="id"
              pagination={false}
            />
          )}
        </Card>
      )}

      {/* 退会は組織単位（owner のみ）の操作のため、メンバー管理の最下部に置く */}
      <WithdrawalPanel organizationId={orgId} />

      <Modal
        title={t('member.invite')}
        open={inviteOpen}
        onOk={handleInvite}
        confirmLoading={invite.isPending}
        onCancel={() => {
          setInviteOpen(false);
          form.resetFields();
        }}
        okText={t('member.sendInvite')}
        cancelText={t('common.cancel') || 'Cancel'}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('member.formName')}
            extra={t('member.formNameNote')}
          >
            <Input prefix={<UserOutlined />} placeholder={t('member.namePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('member.formEmail')}
            rules={[
              { required: true, message: t('member.emailRequired') },
              { type: 'email', message: t('member.emailInvalid') },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="email@example.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
